import { useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface SubscriptionSettingsTabProps {
  subscription: any;
}

export function SubscriptionSettingsTab({ subscription }: SubscriptionSettingsTabProps) {
  
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const formatSubscriptionPeriod = (subscription: any) => {
    if (!subscription) return '구독 정보 없음';
    if (!subscription.current_period_end) return '구독 정보 없음';
    
    const endDate = new Date(subscription.current_period_end * 1000);
    return endDate.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleManageSubscription = () => {
    setIsLoading(true);
    window.location.href = 'https://billing.stripe.com/p/login/eVa2cb4f0gUoaqY7ss';
    
    // Since we're redirecting away, we don't really need to set isLoading back to false,
    // but including a timeout as a fallback in case the redirect fails
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  return (
    <div className="space-y-4 pt-4">
      <Card>
        <CardHeader>
          <CardTitle>구독 정보</CardTitle>
          <CardDescription>
            현재 구독 상태 및 결제 정보
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscription?.cancel_at_period_end && (
            <Alert variant="default" className="bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800/30 dark:text-amber-500">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>구독 취소 예정</AlertTitle>
              <AlertDescription>
                구독이 취소되었습니다. {formatSubscriptionPeriod(subscription)}까지 서비스를 계속 이용할 수 있으며, 이후에는 자동으로 종료됩니다.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="font-medium">
                  {subscription?.subscription_status === 'active' 
                    ? '활성 구독'
                    : subscription?.subscription_status === 'trialing'
                    ? '체험 기간'
                    : '구독 없음'}
                </p>
                {subscription?.subscription_status === 'active' && (
                  <p className="text-sm text-muted-foreground">
                    다음 결제일: {formatSubscriptionPeriod(subscription)}
                  </p>
                )}
                {subscription?.cancel_at_period_end && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                    결제 기간 종료 후 자동 취소됨
                  </p>
                )}
                {subscription?.payment_method_last4 && (
                  <p className="text-sm text-muted-foreground">
                    {subscription.payment_method_brand} •••• {subscription.payment_method_last4}
                  </p>
                )}
              </div>
              {(subscription?.subscription_status === 'active' || 
                subscription?.subscription_status === 'trialing') && (
                <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {subscription?.subscription_status === 'trialing' ? '체험 중' : '활성'}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <p className="font-medium">구독 관리</p>
              <p className="text-sm text-muted-foreground">구독을 변경하거나 취소합니다</p>
            </div>
            <Button
              onClick={handleManageSubscription}
              variant="outline"
              disabled={isLoading}
              className="flex items-center gap-1"
            >
              관리
              <ExternalLink className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
          
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <p className="font-medium">다른 플랜 살펴보기</p>
              <p className="text-sm text-muted-foreground">다양한 구독 플랜을 비교해보세요</p>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              variant="default"
            >
              플랜 보기
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}